import { useQuery } from "@apollo/client/react";
import { Drawer, FormControl, Grid, InputLabel, ListSubheader, Select, Stack, Typography, styled } from "@mui/material";
import { GridColDef, GridFilterItem } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { FC, useState } from "react";
import { FlowPhase, FlowStatus } from "../../constants/flows";
import { DateTimeHuman, Flow, SortDirection, BackfillFlowsDocument } from "../../graphql/types";
import type { BackfillFlowsQuery, BackfillFlowsQueryVariables } from "../../graphql/types";
import { getFormattedReportDate, toLocalAndUtcDates } from "../../lib/Util";
import { RunDetails } from "../RunDetails";
import { Badge } from "../ui/badges";
import { DrawerContainer } from "../ui/common";
import { FlowProgressBar } from "../ui/FlowProgressBar";
import { IconCopyToClipboard } from "../ui/IconCopyToClipboard";
import { MenuItemStyled } from "../ui/rolesChip";
import { CancelledStatus } from "./BackfillDetails";

const RunsContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "50vh",
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.surfaceLight10.main,
  borderRadius: "8px",
  padding: theme.spacing(2),
}));

const StyledAccessTimeIcon = styled(ClockIcon)(({ theme }) => ({
  color: theme.palette.surfaceLight50.main,
}));

const FailedStatusChip = <Badge size="small" severity="error" label="Failed" />;

const hasDirtyRows = (flow: Flow) =>
  flow.inputs?.some((input) => input?.download_part?.dirty_row_count || 0 > 0) ?? false;

const Status = ({ flow }: { flow: Flow }) => {
  if (flow.status === "cancelled") {
    return (
      <Grid>
        <CancelledStatus />
      </Grid>
    );
  }
  if (flow.status === "failed") {
    return <Grid>{FailedStatusChip}</Grid>;
  }

  return (
    <FlowProgressBar
      phase={flow.phase}
      failed={flow.failed}
      orientation="horizontal"
      tight={true}
      hasDirtyRows={hasDirtyRows(flow)}
    />
  );
};

const DrawerBackfillRun = ({
  run,
  onClose,
  showFlowView,
}: {
  run: Flow;
  onClose: () => void;
  showFlowView: boolean;
}) => {
  const { utc } = toLocalAndUtcDates(run.created);

  return (
    <Drawer anchor="right" open={showFlowView} onClose={onClose}>
      <DrawerContainer sx={{ overflowY: "auto" }}>
        <Stack sx={{ alignItems: "center", justifyContent: "flex-start", minWidth: "100%" }} spacing={1}>
          <Stack spacing={1} direction="row" sx={{ alignItems: "center", marginTop: "10px" }}>
            <StyledAccessTimeIcon />
            <Typography sx={{ fontSize: 14 }}>{utc}</Typography>
          </Stack>
          <Stack spacing={1} sx={{ width: "70%", justifyContent: "center" }} direction="row">
            {run.status === "cancelled" ? (
              <CancelledStatus />
            ) : (
              <FlowProgressBar
                phase={run.phase}
                failed={run.failed}
                orientation="horizontal"
                hasDirtyRows={hasDirtyRows(run)}
              />
            )}
          </Stack>
          <RunDetails run={run} />
        </Stack>
      </DrawerContainer>
    </Drawer>
  );
};

const RunsTab: FC<{ backfillId: string }> = ({ backfillId }) => {
  const [selectedRun, setSelectedRun] = useState<Flow | null>(null);
  const [showMoreRowsMessage, setShowMoreRowsMessage] = useState(true);
  const { data, loading } = useQuery<BackfillFlowsQuery, BackfillFlowsQueryVariables>(BackfillFlowsDocument, {
    variables: {
      backfill_id: backfillId,
      sortDirection: SortDirection.Desc,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const rows = data?.flows ? data.flows.edges.map((entry) => entry.node) : [];
  const columns: GridColDef[] = [
    {
      field: "report_datetime",
      headerName: "Report Date",
      editable: false,
      flex: 1,
      filterable: false,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      valueFormatter: (_, row) => {
        return row?.long_utc;
      },
      sortComparator: (v1: DateTimeHuman, v2: DateTimeHuman) => {
        return Date.parse(v1.iso) - Date.parse(v2.iso);
      },
      renderCell: (params) => {
        const row = params.row as Flow;
        return (
          <Stack direction="row" sx={{ alignItems: "center", height: "100%" }}>
            <StyledAccessTimeIcon sx={{ width: "25px", height: "25px" }} data-testid="access-time-icon" />
            <Stack sx={{ marginLeft: "5px" }}>{getFormattedReportDate(row.report_datetime?.iso || null, true)}</Stack>
          </Stack>
        );
      },
    },
    {
      field: "id",
      headerName: "Run ID",
      editable: false,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as Flow;
        return (
          <Grid container sx={{ flexDirection: "row", alignItems: "center" }}>
            <IconCopyToClipboard id={row.id} type="Flow ID" />
          </Grid>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        const row = params.row as Flow;
        return (
          <Stack direction="row" sx={{ alignItems: "center", height: "100%" }}>
            <Status flow={row} />
          </Stack>
        );
      },
      filterOperators: [
        {
          label: "equals",
          value: "equals",
          getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params: string, flow: Flow) => {
              return params === filterItem.value || filterItem.value === flow.phase;
            };
          },
          InputComponent: ({ item, applyValue }) => (
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                sx={{ height: "40px" }}
                labelId="status-label"
                id="status-select"
                data-testid="status-select"
                label="Status"
                variant="outlined"
                value={item?.value || ""}
                onChange={(event) =>
                  applyValue({
                    ...item,
                    value: event.target.value,
                  })
                }
                MenuProps={{
                  slotProps: {
                    paper: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  },
                }}
              >
                <MenuItemStyled key={FlowStatus.Succeeded} value={FlowStatus.Succeeded}>
                  Succeeded
                </MenuItemStyled>
                <MenuItemStyled key={FlowStatus.Failed} value={FlowStatus.Failed}>
                  Failed
                </MenuItemStyled>
                <MenuItemStyled key={FlowStatus.Cancelled} value={FlowStatus.Cancelled}>
                  Cancelled
                </MenuItemStyled>
                <MenuItemStyled key={FlowStatus.Running} value={FlowStatus.Running}>
                  Running
                </MenuItemStyled>
                <MenuItemStyled key={FlowStatus.Queued} value={FlowStatus.Queued}>
                  Queued
                </MenuItemStyled>
                <ListSubheader>Running phase</ListSubheader>
                <MenuItemStyled key={FlowPhase.DOWNLOADING} value={FlowPhase.DOWNLOADING}>
                  Downloading
                </MenuItemStyled>
                <MenuItemStyled key={FlowPhase.PROCESSING} value={FlowPhase.PROCESSING}>
                  Processing
                </MenuItemStyled>
                <MenuItemStyled key={FlowPhase.UPLOADING} value={FlowPhase.UPLOADING}>
                  Uploading
                </MenuItemStyled>
              </Select>
            </FormControl>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <RunsContainer data-testid="backfill-run-list">
        <DataGridPro
          initialState={{
            sorting: {
              sortModel: [{ field: "report_datetime", sort: "desc" }],
            },
          }}
          disableMultipleColumnsFiltering
          rowHeight={60}
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterPagination
          disableColumnPinning
          showToolbar
          slots={{
            footer: () => (
              <>
                {showMoreRowsMessage ? (
                  <Typography
                    variant="body.small.regular"
                    sx={{ color: "text40.main", marginLeft: "auto", paddingTop: "4px" }}
                  >
                    Scroll down to see more rows
                  </Typography>
                ) : null}
              </>
            ),
          }}
          disableDensitySelector
          isCellEditable={() => false}
          loading={loading}
          onRowClick={(params) => {
            setSelectedRun(params.row as Flow);
          }}
          onRowsScrollEnd={() => {
            setShowMoreRowsMessage(false);
          }}
          hideFooterRowCount
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
            row: {
              "data-testid": "backfill-run-row",
            },
          }}
        />
      </RunsContainer>
      {selectedRun ? (
        <DrawerBackfillRun run={selectedRun} onClose={() => setSelectedRun(null)} showFlowView={!!selectedRun} />
      ) : null}
    </>
  );
};

export { RunsTab };
